import { useEffect, useState } from "react";

import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@mui/material";
import { PageContainer } from "@/features/ui";

import { PurchaseInvoiceForm } from "@/features/purchase-invoices";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import { CenteredLoading, useSnackbar } from "@/features/ui";

export const EditPurchaseInvoice = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [defaultValues, setDefaultValues] = useState(null);

  useEffect(() => {
    const fetchPurchaseInvoice = async () => {
      setIsLoading(true);
      try {
        const {
          data: { data },
        } = await apiOutletClient.get(`purchase-invoices/${referenceNumber}`, {
          params: {
            withBuyer: true,
            withSeller: true,
            withSellerBankAccount: true,
            withPaymentTerm: true,
            withProductVariants: true,
            withPurchaseInvoiceExpenses: true,
            withCreator: true,
            withPurchaseDownPaymentAllocations: true,
          },
        });

        setDefaultValues({
          transactionDate: dayjs(data.transactionDate),
          warehouse: data.warehouse,
          buyer: data.buyer,
          seller: data.seller,
          sellerableType: data.sellerableType,
          sellerReferenceNumber: data.sellerReferenceNumber,
          sellerBankAccount: data.purchaseInvoiceSellerBankAccount
            ? {
                accountNumber:
                  data.purchaseInvoiceSellerBankAccount.accountNumber,
                bank: data.purchaseInvoiceSellerBankAccount.bank,
              }
            : null,
          paymentTerm: data.paymentTerm,
          note: data.note,
          taxable: data.taxable,
          totalIncludesTax: data.totalIncludesTax,
          taxAmount: data.taxAmount,
          taxPercentage: data.taxPercentage,
          discountInput: data.discountAmount || 0,
          discountType: data.discountType,
          purchaseInvoiceProductVariants:
            data.purchaseInvoiceProductVariants.map((variant) => ({
              id: variant.id,
              goodsReceiptProductVariantId:
                variant.goodsReceiptProductVariantId,
              name: variant.name,
              sku: variant.sku,
              unitOfMeasurement: variant.product.unitOfMeasurement.name,
              quantity: variant.quantity,
              discount: variant.discount,
              price: variant.price,
              note: variant.note,
              returnedQuantity: variant.returnedQuantity,
            })),
          purchaseInvoiceExpenses:
            data.purchaseInvoiceExpenses?.map((expense) => ({
              outletChartOfAccount: expense.outletChartOfAccount,
              amount: expense.amount,
              note: expense.note,
              returnedAmount: expense.returnedAmount,
            })) || [],
          purchaseDownPaymentAllocations:
            data.purchaseDownPaymentAllocations?.map((allocation) => {
              const sourcePdp = allocation.sourcePurchaseInvoice || {};
              const ownAmount = Number(allocation.amount ?? 0);
              // Ceiling for this row: what the PDP still has free (excluding
              // this invoice) plus what this invoice already holds. The detail
              // read does not expose the remaining figure yet, so today this
              // resolves to ownAmount — the row can be lowered or removed but
              // not raised, and it lifts on its own once the API supplies it.
              const remaining = Number(sourcePdp.availableAmount ?? 0);
              return {
                id: allocation.id,
                isNewlyAdded: false,
                sourcePurchaseInvoiceId:
                  allocation.sourcePurchaseInvoiceId ?? sourcePdp.id,
                referenceNumber: sourcePdp.referenceNumber,
                availableAmount: remaining + ownAmount,
                amount: ownAmount,
              };
            }) || [],
        });
      } catch (error) {
        showSnackbar(
          getErrorMessage(error, "Gagal memperbarui faktur pembelian"),
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchPurchaseInvoice();
  }, [referenceNumber, showSnackbar]);

  const handleSubmit = async (formData) => {
    try {
      const { buyerId, ...payload } = formData;

      await apiOutletClient.put(
        `purchase-invoices/${referenceNumber}`,
        payload,
        {
          headers: {
            "Outlet-Id": buyerId,
          },
        }
      );
      navigate(-1);
    } catch (error) {
      showSnackbar(
        getErrorMessage(error, "Gagal memperbarui faktur pembelian"),
      );
    }
  };

  if (!defaultValues && isLoading) {
    return <CenteredLoading />;
  }

  return (
    <PageContainer
      maxWidth="xl"
      title="Ubah Faktur Pembelian"
      breadcrumbs={[
        { title: "Faktur Pembelian", path: "/procurement/purchase-invoices" },
        {
          title: referenceNumber,
          path: `/procurement/purchase-invoices/${referenceNumber}`,
        },
        { title: "Ubah" },
      ]}
    >
      <PurchaseInvoiceForm
        editMode={true}
        isLoading={isLoading}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        renderActionButton={({ isSubmitting }) => (
          <Button type="submit" variant="contained" loading={isSubmitting}>
            Simpan
          </Button>
        )}
      />
    </PageContainer>
  );
};
